/**
 * Utility functions for handling offer images
 */

/**
 * Detects if the current device is mobile
 * @returns {boolean} true if mobile, false if desktop
 */
export const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * Gets an image from the images array based on type and device
 * @param {Array<string>} images - Array of image URLs
 * @param {string} imageType - Type of image: 'detail-img', 'store-img', 'card-img', 'carousel-img', 'eligible-card-img'
 * @param {string|null} device - 'web' or 'mobile', or null to auto-detect
 * @returns {string|null} The matching image URL or null if not found
 */
export const getImageByType = (images, imageType, device = null) => {
  if (!images || !Array.isArray(images) || images.length === 0) {
    return null;
  }

  // Auto-detect device if not provided
  const targetDevice = device || (isMobileDevice() ? 'mobile' : 'web');

  // Filter images by type
  const typePattern = new RegExp(`/${imageType}-${targetDevice}/`, 'i');
  const matchingImage = images.find(img => 
    img && typeof img === 'string' && typePattern.test(img)
  );

  if (matchingImage) {
    return matchingImage;
  }

  // Fallback: try the other device if exact match not found
  const fallbackDevice = targetDevice === 'web' ? 'mobile' : 'web';
  const fallbackPattern = new RegExp(`/${imageType}-${fallbackDevice}/`, 'i');
  const fallbackImage = images.find(img => 
    img && typeof img === 'string' && fallbackPattern.test(img)
  );

  if (fallbackImage) {
    return fallbackImage;
  }

  // Final fallback: any image with the type (regardless of device)
  const anyDevicePattern = new RegExp(`/${imageType}-`, 'i');
  const anyDeviceImage = images.find(img => 
    img && typeof img === 'string' && anyDevicePattern.test(img)
  );

  return anyDeviceImage || null;
};

/**
 * Gets both web and mobile images for responsive display
 * @param {Array<string>} images - Array of image URLs
 * @param {string} imageType - Type of image
 * @returns {Object} Object with web and mobile image URLs
 */
export const getResponsiveImages = (images, imageType) => {
  if (!images || !Array.isArray(images) || images.length === 0) {
    return { web: null, mobile: null };
  }

  const typePattern = (device) => new RegExp(`/${imageType}-${device}/`, 'i');
  
  const webImage = images.find(img => 
    img && typeof img === 'string' && typePattern('web').test(img)
  );
  
  const mobileImage = images.find(img => 
    img && typeof img === 'string' && typePattern('mobile').test(img)
  );

  return {
    web: webImage || null,
    mobile: mobileImage || null,
  };
};

export const getImageAssetValue = (asset, preferredKeys = []) => {
  if (!asset) return null;

  if (typeof asset === 'string') {
    return asset;
  }

  if (Array.isArray(asset)) {
    for (const item of asset) {
      const value = getImageAssetValue(item, preferredKeys);
      if (value) return value;
    }
    return null;
  }

  if (typeof asset === 'object') {
    const keys = [
      ...preferredKeys,
      'filename',
      'fileName',
      'name',
      'url',
      'src',
      'path',
      'image',
      'web',
      'desktop',
      'mobile',
    ];

    for (const key of keys) {
      const value = getImageAssetValue(asset[key], preferredKeys);
      if (value) return value;
    }
  }

  return null;
};

export const getCloudfrontAssetUrl = (asset) => {
  const value = getImageAssetValue(asset);
  if (!value) return null;

  if (/^https?:\/\//i.test(value) || value.startsWith('/')) {
    return value;
  }

  return `${process.env.REACT_APP_CLOUDFRONT || ''}${value}`;
};

export const getTenantBannerImageUrl = (
  images,
  imageKey,
  tenantUrlName,
  prefix,
) => {
  const value = getImageAssetValue(images?.[imageKey], [
    prefix,
    prefix === 'mob' ? 'mobile' : 'desktop',
    prefix === 'web' ? 'web' : '',
  ]);

  if (!value) return null;

  if (/^https?:\/\//i.test(value) || value.startsWith('/')) {
    return value;
  }

  const filename = value.startsWith(`${prefix}_`) ? value : `${prefix}_${value}`;
  return `${process.env.REACT_APP_CLOUDFRONT || ''}tenantbannerimages/${tenantUrlName}/${filename}`;
};

/**
 * Gets multiple images by type (useful for carousels)
 * @param {Array<string>} images - Array of image URLs
 * @param {string} imageType - Type of image
 * @param {string|null} device - 'web' or 'mobile', or null to auto-detect
 * @returns {Array<string>} Array of matching image URLs
 */
export const getImagesByType = (images, imageType, device = null) => {
  if (!images || !Array.isArray(images) || images.length === 0) {
    return [];
  }

  const targetDevice = device || (isMobileDevice() ? 'mobile' : 'web');
  const typePattern = new RegExp(`/${imageType}-${targetDevice}/`, 'i');
  
  const matchingImages = images.filter(img => 
    img && typeof img === 'string' && typePattern.test(img)
  );

  if (matchingImages.length > 0) {
    return matchingImages;
  }

  // Fallback: try the other device
  const fallbackDevice = targetDevice === 'web' ? 'mobile' : 'web';
  const fallbackPattern = new RegExp(`/${imageType}-${fallbackDevice}/`, 'i');
  const fallbackImages = images.filter(img => 
    img && typeof img === 'string' && fallbackPattern.test(img)
  );

  if (fallbackImages.length > 0) {
    return fallbackImages;
  }

  // Final fallback: any images with the type
  const anyDevicePattern = new RegExp(`/${imageType}-`, 'i');
  return images.filter(img => 
    img && typeof img === 'string' && anyDevicePattern.test(img)
  );
};
